import t from 'react-translate';
import { useSelector } from 'react-redux';
import { useContext, useEffect, useState } from 'react';
import NvSwitch from 'shared/components/inputs/nv-switch';
import { ReactComponent as AutoMatchingIcon } from 'styles/icons/auto-generated-connections.svg';
import { css } from '@emotion/react';
import { black, gray2, gray7, primary, purple } from 'styles/global_defaults/colors';
import {
  halfSpacing,
  standardSpacing,
  threeQuartersSpacing,
} from 'styles/global_defaults/scaffolding';
import { useAppDispatch } from 'redux/store';
import {
  setAutoMatchingStatus,
  updateMentoringProgram,
} from 'redux/actions/mentoring-programs';
import { Link } from 'react-router-dom';
import { getMentorshipProgramAutoMatchingRulesTotalCount } from 'redux/actions/mentoring-program-matching-rules';
import { getAutoMatchingRulesTotalCount } from 'redux/selectors/mentorship-program-matching-rules';
import NvTooltip from 'shared/components/nv-tooltip';
import NvIcon from 'shared/components/nv-icon';
import { headerLineHeight } from 'styles/global_defaults/fonts';
import { config } from '@config/pendo.config.json';
import MentoringProgramContext from '../../mentoring-program/context';
import AnnouncementDateNotification from './announcement-date-notification';

const styles = css`
  color: ${black};
  gap: ${halfSpacing}px;

  .title {
    line-height: ${headerLineHeight}px;
  }
  .wrapper {
    padding: ${halfSpacing}px ${standardSpacing}px;
    border-radius: ${halfSpacing}px;
    background-color: ${gray7};
  }
  .content {
    margin-top: 14px;
    color: ${gray2};
  }
  .create-rules {
    margin: 14px 0 ${halfSpacing}px 0;
  }
  .link {
    color: ${primary};
  }
  .toggle-auto-match {
    gap: ${threeQuartersSpacing}px;
  }
  .toggle-info {
    color: ${primary};
  }
  .checked-background {
    background: linear-gradient(90deg, ${primary}, ${purple}) !important;
  }
`;

const AutoMatchingModal = () => {
  const dispatch = useAppDispatch();
  const { mentoringProgram } = useContext(MentoringProgramContext);
  const {
    id,
    autoMatchingEnabled,
    concurrentMentorConnection,
    description,
    matchAnnouncementDate,
  } = mentoringProgram || {};
  const [isAutoMatchingEnabled, setIsAutoMatchingEnabled] =
    useState<boolean>(autoMatchingEnabled);
  const autoMatchingRulesCount = useSelector(getAutoMatchingRulesTotalCount);
  const dataQATag = config.pendo.athena.mentorshipProgram.autoMatchingModal;
  const translationBase =
    t.MENTORING_PROGRAMS.PROGRAM_HEADER_MENU.AUTO_MATCHING.MODAL;

  useEffect(() => {
    dispatch(
      getMentorshipProgramAutoMatchingRulesTotalCount({
        mentorshipProgramId: id,
      })
    );
  }, []);

  const handleToggleAutoMatching = (newState: boolean) => {
    setIsAutoMatchingEnabled(newState);
    dispatch(
      updateMentoringProgram({
        id,
        autoMatchingEnabled: newState,
        description,
        maxMentees: concurrentMentorConnection,
      })
    );
    if (!newState) {
      dispatch(setAutoMatchingStatus({ autoMatchingStatus: null }));
    }
  };

  const getLinkToRulesSettings = (text: string, ariaLabel: string) => (
    <Link
      to='/settings?tab=matching'
      className='link bold text-large-body'
      data-qa={dataQATag.matchingRulesLink}
      aria-label={ariaLabel}
    >
      {text}
    </Link>
  );

  const totalRulesAppliedLabel = translationBase.RULES.TOTAL_RULES_APPLIED(
    autoMatchingRulesCount
  );
  const createRulesLabel = translationBase.RULES.CREATE_RULES();
  const createRulesAriaLabel = translationBase.RULES.ARIA_LABEL.CREATE();
  const editRulesLinkLabel = translationBase.RULES.EDIT();
  const editRulesAriaLabel = translationBase.RULES.ARIA_LABEL.EDIT();

  return (
    <div css={styles} className='d-flex flex-column'>
      <div className='heading-5 text-align-center pt-2 title'>
        {translationBase.TITLE()}
      </div>
      <div className='d-flex justify-content-between align-items-center wrapper'>
        <div className='d-flex toggle-auto-match'>
          <NvSwitch
            checked={isAutoMatchingEnabled}
            onChange={handleToggleAutoMatching}
            enabledLabel={t.SHARED.ON().toUpperCase()}
            disabledLabel={t.SHARED.OFF().toUpperCase()}
            dataQa={dataQATag.autoMatchingSwitch}
            ariaLabel={
              isAutoMatchingEnabled
                ? translationBase.ARIA_LABEL.DISABLE_AUTO_MATCHING()
                : translationBase.ARIA_LABEL.ENABLE_AUTO_MATCHING()
            }
          />
          <span className='text-regular'>
            {translationBase.TOGGLE_LABEL(`${isAutoMatchingEnabled}`)}
          </span>
        </div>
        <NvTooltip text={translationBase.TOGGLE_INFO()}>
          <NvIcon icon='info' className='toggle-info' size='smallest' tabIndex={0} />
        </NvTooltip>
      </div>
      {autoMatchingRulesCount ? (
        <div className='d-flex justify-content-between wrapper'>
          <span className='bold text-large-body'>{totalRulesAppliedLabel}</span>
          {getLinkToRulesSettings(editRulesLinkLabel, editRulesAriaLabel)}
        </div>
      ) : (
        <div className='d-flex flex-column align-items-center wrapper'>
          <AutoMatchingIcon width={145} height={34} className='mt-2' />
          <span className='content text-small'>
            {translationBase.CONTENT()}
          </span>
          <span className='create-rules'>
            {getLinkToRulesSettings(createRulesLabel, createRulesAriaLabel)}
          </span>
        </div>
      )}
      <AnnouncementDateNotification announcementDate={matchAnnouncementDate} />
    </div>
  );
};

export default AutoMatchingModal;
